import type { JSX } from 'react';
import { FormField } from '@/components/form';
import { CANADIAN_POSTAL_CODE_PATTERN } from '@/lib/constants/validationPatterns';
import {
  PROVINCE_OPTIONS,
  STREET_DIRECTION_OPTIONS,
  STREET_TYPE_OPTIONS,
} from '@/routes/dashboard/propertyCreate/options';
import type {
  PropertyEditFieldChangeFactory,
  PropertyEditFormErrors,
  PropertyEditFormState,
} from './types';

interface PropertyEditAddressSectionProps {
  formState: PropertyEditFormState;
  errors: PropertyEditFormErrors;
  disabled: boolean;
  inputClass: string;
  onFieldChange: PropertyEditFieldChangeFactory;
}

export function PropertyEditAddressSection({
  formState,
  errors,
  disabled,
  inputClass,
  onFieldChange,
}: PropertyEditAddressSectionProps): JSX.Element {
  return (
    <section className="bg-surface-muted/30 rounded-2xl border border-surface-subtle p-4">
      <h2 className="text-sm font-semibold text-text-primary">Address</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <FormField
          id="street-number"
          label="Street number"
          description="Street number or range. Example: 501 or 501-503."
          required
          error={errors.streetNumber}
        >
          <input
            value={formState.streetNumber}
            onChange={onFieldChange('streetNumber')}
            maxLength={16}
            className={inputClass}
            disabled={disabled}
          />
        </FormField>
        <FormField
          id="street-name"
          label="Street name"
          description="Street name without the suffix. Example: Queen."
          required
          error={errors.streetName}
        >
          <input
            value={formState.streetName}
            onChange={onFieldChange('streetName')}
            maxLength={120}
            className={inputClass}
            disabled={disabled}
          />
        </FormField>
        <FormField
          id="street-type"
          label="Street type"
          description="Street suffix used in the public address (St, Ave, Rd)."
          required
          error={errors.streetType}
        >
          <select
            value={formState.streetType}
            onChange={onFieldChange('streetType')}
            className={inputClass}
            disabled={disabled}
          >
            <option value="" disabled>
              Select street type
            </option>
            {STREET_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          id="street-direction"
          label="Street direction (optional)"
          description="Optional directional suffix (N, NE, SW)."
          error={errors.streetDirection}
        >
          <select
            value={formState.streetDirection}
            onChange={onFieldChange('streetDirection')}
            className={inputClass}
            disabled={disabled}
          >
            <option value="">None</option>
            {STREET_DIRECTION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          id="suite-number"
          label="Suite number (optional)"
          description="Optional building suite if needed. Example: 100."
          error={errors.suiteNumber}
        >
          <input
            value={formState.suiteNumber}
            onChange={onFieldChange('suiteNumber')}
            maxLength={16}
            className={inputClass}
            disabled={disabled}
          />
        </FormField>
        <FormField
          id="city"
          label="City"
          description="Public address city."
          required
          error={errors.city}
        >
          <input
            value={formState.city}
            onChange={onFieldChange('city')}
            maxLength={120}
            className={inputClass}
            disabled={disabled}
          />
        </FormField>
        <FormField
          id="province"
          label="Province"
          description="Two-letter province code shown publicly."
          required
          error={errors.province}
        >
          <select
            value={formState.province}
            onChange={onFieldChange('province')}
            className={inputClass}
            disabled={disabled}
          >
            <option value="" disabled>
              Select province
            </option>
            {PROVINCE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          id="postal-code"
          label="Postal code"
          description="Canadian format A1A 1A1."
          required
          error={errors.postalCode}
        >
          <input
            value={formState.postalCode}
            onChange={onFieldChange('postalCode')}
            maxLength={7}
            pattern={CANADIAN_POSTAL_CODE_PATTERN}
            className={inputClass}
            disabled={disabled}
          />
        </FormField>
      </div>
    </section>
  );
}
